/**
 * Tickets (TMMPDM-0109; TMMAPI-0181 U5): the words and small rules the list and a ticket share.
 * Nothing here decides from two server fields: a ticket's number and type make its reference
 * (formatting), and a status is worded by its own code.
 */
import { ApiError } from '@/api/errors';
import { FEATURE_REQUEST_STATUSES, SUPPORT_CASE_STATUSES, type TicketType } from '@/api/tmm/types';

export type Translate = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

/** The list's tabs, one per type (URL `tab`). */
export const TICKET_TABS = ['support', 'features'] as const;
export type TicketTab = (typeof TICKET_TABS)[number];

export const TAB_TYPE: Record<TicketTab, TicketType> = {
  support: 'SUPPORT_CASE',
  features: 'FEATURE_REQUEST',
};

const PREFIX: Record<TicketType, string> = { SUPPORT_CASE: 'SC', FEATURE_REQUEST: 'FR' };

/** "SC-1001", "FR-1002": the number with its type's prefix. A type this console doesn't know shows the number. */
export function ticketRef(type: string, number: number): string {
  const prefix = Object.hasOwn(PREFIX, type) ? PREFIX[type as TicketType] : null;
  return prefix === null ? String(number) : `${prefix}-${number}`;
}

/** The number in a `/tickets/:number` path: "SC-1001", "fr-1002" or "1002". Anything else is null. */
export function parseTicketNumber(raw: string): number | null {
  const match = /^(?:(?:SC|FR)-)?(\d{1,15})$/i.exec(raw.trim());
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

/** The statuses of one type, in the contract's order. */
export function statusesOf(type: string): readonly string[] {
  if (type === 'SUPPORT_CASE') return SUPPORT_CASE_STATUSES;
  if (type === 'FEATURE_REQUEST') return FEATURE_REQUEST_STATUSES;
  return [];
}

const STATUS_FALLBACK: Record<string, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  WAITING_ON_YOU: 'Waiting on the member',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  SUBMITTED: 'Submitted',
  UNDER_REVIEW: 'Under review',
  PLANNED: 'Planned',
  SHIPPED: 'Shipped',
  NOT_PLANNED: 'Not planned',
  DUPLICATE: 'Duplicate',
};

/** A status's label; one this console doesn't know shows its code. */
export function statusLabel(status: string, t: Translate): string {
  return t(`tickets.status.${status}`, STATUS_FALLBACK[status] ?? status);
}

/** "Support case" or "Feature request". */
export function typeLabel(type: string, t: Translate): string {
  if (type === 'SUPPORT_CASE') return t('tickets.type.SUPPORT_CASE', 'Support case');
  if (type === 'FEATURE_REQUEST') return t('tickets.type.FEATURE_REQUEST', 'Feature request');
  return type;
}

/**
 * A 404 that names nothing it couldn't find: tmm-api hasn't deployed the ticket routes yet (an
 * unmapped route answers `NOT_FOUND`, or no code). `TICKET_NOT_FOUND` is a deployed route's answer.
 */
export function isNotDeployed(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 404 && String(error.code) !== 'TICKET_NOT_FOUND'
  );
}

export function isTicketNotFound(error: unknown): boolean {
  return error instanceof ApiError && String(error.code) === 'TICKET_NOT_FOUND';
}

export function notAvailableText(t: Translate): string {
  return t('tickets.notAvailable', "Tickets aren't available yet.");
}

/** Created-range presets. "Any time" sends no date. */
export const CREATED_RANGES = ['ANY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'LAST_90_DAYS'] as const;
export type CreatedRange = (typeof CREATED_RANGES)[number];

const RANGE_DAYS: Record<Exclude<CreatedRange, 'ANY'>, number> = {
  LAST_7_DAYS: 7,
  LAST_30_DAYS: 30,
  LAST_90_DAYS: 90,
};

/** The start of the local day `days - 1` days before `today`, as an instant; undefined for Any time. */
export function createdFrom(range: CreatedRange, today: Date = new Date()): string | undefined {
  if (range === 'ANY') return undefined;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
  start.setDate(start.getDate() - (RANGE_DAYS[range] - 1));
  return start.toISOString();
}

/** The person who asked: their name, else their email, else "—". The id is never shown. */
export function requesterOf(row: { requesterName?: string; requesterEmail?: string }): string {
  if (row.requesterName !== undefined && row.requesterName !== '') return row.requesterName;
  if (row.requesterEmail !== undefined && row.requesterEmail !== '') return row.requesterEmail;
  return '—';
}

/** The tenant by name: as the ticket sent it, else the console's tenant list, else its slug. */
export function tenantNameOf(
  row: { tenantSlug: string; tenantName?: string },
  names: ReadonlyMap<string, string>,
): string {
  if (row.tenantName !== undefined && row.tenantName !== '') return row.tenantName;
  return names.get(row.tenantSlug) ?? row.tenantSlug;
}

/** A tenant's partner page. */
export function tenantPath(slug: string): string {
  return `/partners/by-tenant/${encodeURIComponent(slug)}`;
}

export const BODY_MAX = 4000;

/** TMMPDM-0110: an attachment's name, safe to save under: no path or control characters; never empty. */
export function downloadName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  // eslint-disable-next-line no-control-regex
  const clean = base.replace(/[\u0000-\u001f\u007f<>:"|?*]/g, '_').trim();
  return clean === '' || /^\.+$/.test(clean) ? 'attachment' : clean;
}
